// `LazyCandidateContainerService` / `LazyClientContactContainerService` — "the entity THIS page
// is about", for the two analytics paths that ask for it without owning the screen: the page view
// (`data.getShortlistAnalyticsExtraInfo`, `extension.state.tabs.config.ng1.ts:360`, `:1805`) and
// `ShortlistOpened` (`shortlist.status.service.ng1.ts:79`).
//
// Upstream resolves a `candidateContainer` / `clientContactContainer` on four states and reads it
// back off `$state.$current`'s resolved locals; a page under none of them has no entity and the
// lazy service answers `undefined`. Here the profile screens fetch their own entity, so they
// PUBLISH it, and the analytics side WAITS for the one the URL names — which is upstream's
// `await candidateContainer.getCandidate()`: the page view of a profile goes out once the profile
// has loaded, carrying its algorithm score, and never if it fails to load.
//
// ⚠️ THE PATH DECIDES WHETHER THERE IS AN ENTITY, NOT WHETHER ONE WAS PUBLISHED. A list page
// (`/shortlist/review?…&candidateId=…`) carries an id and still has no container upstream, so it
// tracks with no entity rather than with whichever profile happened to load last.

const BASE = '/chrome-extension/extension'

export type PageEntityKind = 'candidate' | 'clientContact'

/** The states that resolve a container, as path prefixes: `shortlist.candidate`, `shortlist.hunter.review.candidate`, and the contact twins. */
const CONTAINER_PREFIXES: Record<PageEntityKind, readonly string[]> = {
  candidate: [`${BASE}/shortlist/candidate/`, `${BASE}/shortlist/review/candidate/`],
  clientContact: [`${BASE}/client-contact/client-contact/`, `${BASE}/client-contact/review/client-contact/`],
}

/** The `$stateParams` key each container fetches by. */
const ID_PARAM: Record<PageEntityKind, string> = { candidate: 'candidateId', clientContact: 'clientContactId' }

type Entity = { id: number } & object

const MAX_WAITING = 20

const latest: Partial<Record<PageEntityKind, Entity>> = {}
const waiting: Array<{ kind: PageEntityKind; id: number; resolve: (entity: Entity) => void }> = []

/** A profile screen has its entity (again). Resolves anything waiting for that id. */
export function publishPageEntity(kind: PageEntityKind, entity: Entity): void {
  latest[kind] = entity
  for (let i = waiting.length - 1; i >= 0; i -= 1) {
    const waiter = waiting[i]
    if (waiter === undefined || waiter.kind !== kind || waiter.id !== entity.id) continue
    waiting.splice(i, 1)
    waiter.resolve(entity)
  }
}

/** Whether `pathname` is under a state that resolves this kind's container. */
export function hasContainer(kind: PageEntityKind, pathname: string): boolean {
  return CONTAINER_PREFIXES[kind].some((prefix) => pathname.startsWith(prefix))
}

/**
 * `lazy…ContainerService.get…()` for the page at `location`: `undefined` off the container states,
 * otherwise the entity once its profile has published it. `parseInt`, as `$stateParams` gives the
 * container a number — an id the URL does not carry is an entity that never arrives.
 */
export function pageEntity(kind: PageEntityKind, location: { pathname: string; search: string }): Promise<Entity | undefined> {
  if (!hasContainer(kind, location.pathname)) return Promise.resolve(undefined)
  const id = Number.parseInt(new URLSearchParams(location.search).get(ID_PARAM[kind]) ?? '', 10)
  const known = latest[kind]
  if (known !== undefined && known.id === id) return Promise.resolve(known)
  if (Number.isNaN(id)) return new Promise(() => undefined)
  return new Promise((resolve) => {
    waiting.push({ kind, id, resolve })
    // A profile that never loads leaves its waiter behind; upstream's rejected promise is
    // collected, so bound this rather than hold every failed page for the life of the frame.
    if (waiting.length > MAX_WAITING) waiting.shift()
  })
}
